/**
 * api/techScope.js — Fase 3 (Tecnologia) · Bloco 2.
 *
 * Blueprint: GET /projects/{id}/tech-blueprint (wizard) + POST .../generate (Claude).
 * Gates de fase: GET/PATCH /projects/{id}/journey-gates/{gateKey}.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

export function useTechBlueprint(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['tech-blueprint', projectId],
    queryFn:  async () => {
      try { return await api.get(`/projects/${projectId}/tech-blueprint`); }
      catch { return null; }
    },
    enabled: Boolean(projectId),
  });
}

/**
 * useDetectedStack — stack REAL detectada por repositório (papel + linguagens).
 * Em brownfield, o blueprint pré-preenche e TRAVA a partir disto (nada de trocar).
 * { projectType, brownfield, repos: [{repo, role, languages}], languages }
 */
export function useDetectedStack(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['detected-stack', projectId],
    queryFn:  async () => {
      try { return await api.get(`/projects/${projectId}/detected-stack`); }
      catch { return null; }
    },
    enabled: Boolean(projectId),
  });
}

export function useGenerateBlueprint(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    // body opcional { languages, frameworks, infrastructure, databases } — se
    // vier, a IA monta a arquitetura HONRANDO a stack escolhida pelo time.
    meta: { aiLabel: 'A IA está montando o Blueprint técnico…', aiSublabel: 'Compondo a arquitetura-alvo e os artefatos, respeitando a stack escolhida.' },
    mutationFn: (body = {}) => api.post(`/projects/${projectId}/tech-blueprint/generate`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['tech-blueprint', projectId] }),
  });
}

/** useSaveBlueprint — persiste a stack escolhida (PUT, sem IA) e grava na memória. */
export function useSaveBlueprint(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put(`/projects/${projectId}/tech-blueprint`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['tech-blueprint', projectId] }),
  });
}

/** Sugere opções de stack por frente (IA) para o time escolher no Blueprint. */
export function useSuggestBlueprintOptions(projectId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está sugerindo opções de stack…', aiSublabel: 'Analisando o escopo para recomendar linguagens, frameworks, infra e bancos.' },
    mutationFn: () => api.post(`/projects/${projectId}/tech-blueprint/suggest-options`, {}),
  });
}

/** Recomenda/auto-seleciona os serviços da cloud escolhida (coerentes com escopo + stack). */
export function useRecommendServices(projectId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está selecionando os serviços da cloud…',
            aiSublabel: 'Escolhendo os serviços gerenciados coerentes com o escopo e a stack.' },
    mutationFn: (body) => api.post(`/projects/${projectId}/tech-blueprint/recommend-services`, body),
  });
}

/** Detecta conflitos no blueprint e devolve a stack corrigida (correção via IA). */
export function useBlueprintConflicts(projectId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está verificando conflitos do Blueprint…', aiSublabel: 'Checando a stack e a arquitetura contra o escopo e os documentos.' },
    mutationFn: () => api.post(`/projects/${projectId}/tech-blueprint/conflicts`, {}),
  });
}

/**
 * Catálogo CANÔNICO de artefatos, servido pela API (itens 34/N32).
 *
 * A tela mantinha o seu próprio catálogo hardcoded e mandava o rótulo em texto
 * livre ao gerar — 10 dos 15 rótulos não casavam o catálogo real do backend, e
 * a seleção do usuário era descartada em silêncio. Lendo daqui, tela, gerador e
 * auditor falam a mesma chave.
 */
export function useArtifactCatalog() {
  const api = useApi();
  return useQuery({
    queryKey: ['artifact-catalog'],
    queryFn:  () => api.get('/artifacts/catalog'),
    staleTime: 60 * 60 * 1000,   // o catálogo muda com deploy, não com o uso
  });
}

export function useArtifacts(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['artifacts', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/artifacts`),
    enabled:  Boolean(projectId),
  });
}

export function useGenerateArtifacts(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está gerando os artefatos técnicos…', aiSublabel: 'Produzindo ADRs, diagramas C4/sequência, topologia e Terraform a partir do Blueprint.' },
    mutationFn: (selected = []) => api.post(`/projects/${projectId}/artifacts/generate`, { selected }),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['artifacts', projectId] }),
  });
}

export function useDataTech(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['data-tech', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/data-tech`),
    enabled:  Boolean(projectId),
  });
}

export function useAnalyzeDataTech(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    // body opcional { feedback } — orientação p/ RESOLVER conflitos.
    mutationFn: (body = {}) => api.post(`/projects/${projectId}/data-tech/analyze`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['data-tech', projectId] }),
  });
}

// Resolver conflitos com IA (item 6): PROPÕE (preview, não salva) → usuário revisa →
// APLICA o texto que ele deixar. Espelha o padrão de "Resolver itens parciais".
export function useResolveDataTech(projectId) {
  const api = useApi();
  return useMutation({
    mutationKey: ['ai'],
    // body opcional { feedback }; retorna { resolution, conflicts } — NÃO salvo.
    mutationFn: (body = {}) => api.post(`/projects/${projectId}/data-tech/resolve`, body),
  });
}

export function useApplyDataTechResolution(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    // salva o texto (revisado/editado pelo usuário) e LIMPA os conflitos.
    mutationFn: (resolution) =>
      api.post(`/projects/${projectId}/data-tech/apply-resolution`, { resolution }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-tech', projectId] }),
  });
}

export function useComponents(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['components', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/components`),
    enabled:  Boolean(projectId),
  });
}

export function useSuggestComponents(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['ai'],
    meta: { aiLabel: 'A IA está buscando componentes reutilizáveis…', aiSublabel: 'Vasculhando outros projetos do tenant e validando o reuso contra o escopo atual.' },
    mutationFn: () => api.post(`/projects/${projectId}/components/suggest`, {}),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['components', projectId] }),
  });
}

export function useToggleComponent(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, selected }) => api.patch(`/projects/${projectId}/components/${id}`, { selected }),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['components', projectId] }),
  });
}

/**
 * useSquadDesign — composição do squad desenhada (Fase 4 · setup).
 * GET /projects/{id}/squad-design → { project_id, design|null }.
 */
export function useSquadDesign(projectId) {
  const api = useApi();
  return useQuery({
    queryKey: ['squad-design', projectId],
    queryFn:  async () => {
      try { return await api.get(`/projects/${projectId}/squad-design`); }
      catch { return null; }
    },
    enabled: Boolean(projectId),
  });
}

/** useSaveSquadDesign — persiste a composição (PUT) em working_state.squad_design. */
export function useSaveSquadDesign(projectId) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (design) => api.put(`/projects/${projectId}/squad-design`, { design }),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['squad-design', projectId] });
      qc.invalidateQueries({ queryKey: ['squad-conferencia', projectId] });
    },
  });
}

/**
 * useSquadConferencia — conferência do squad (A2 FR-6): por membro o selo do prompt,
 * o modelo e o perfil efetivos, as skills com `tem_conteudo` e o custo; por etapa o
 * responsável e a origem; alertas, bloqueios, contract_mode e pode_aprovar.
 * GET /projects/{id}/squad-design/conferencia → ConferenciaSquadOut. Só leitura;
 * sempre relê o design salvo (EC-8).
 */
export function useSquadConferencia(projectId, { enabled = true } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['squad-conferencia', projectId],
    queryFn:  () => api.get(`/projects/${projectId}/squad-design/conferencia`),
    enabled:  Boolean(projectId) && enabled,
    staleTime: 5_000,
    retry: false,
  });
}

export function useJourneyGate(projectId, gateKey) {
  const api = useApi();
  return useQuery({
    queryKey: ['journey-gate', projectId, gateKey],
    queryFn:  () => api.get(`/projects/${projectId}/journey-gates/${gateKey}`),
    enabled:  Boolean(projectId && gateKey),
  });
}

export function useDecideJourneyGate(projectId, gateKey) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch(`/projects/${projectId}/journey-gates/${gateKey}`, body),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['journey-gate', projectId, gateKey] });
      qc.invalidateQueries({ queryKey: ['project', projectId, 'journey'] });
    },
  });
}
